import {ReloadOutlined} from '@ant-design/icons';
import {Flex, Button, Form} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import Loader from '../../../components/Loader/Loader';
import PageContent from '../../../components/PageContent/PageContent';
import MainTableTitle from '../../../components/Table/Utils/MainTableTitle';
import type {LciFormValues} from '../../../services/interface/pcfTypes';
import usePCFStore from '../../../store/pcfStore/pcfStore';
import {
  LCI_FIELD_PREFIX_TO_SUBTAB,
  lciFormInitialValues,
  type LciFormTabKey
} from '../constants/lciFormValues';
import usePCFPersist from '../hooks/usePCFPersist';
import {usePCFStepHydration} from '../hooks/usePCFStepHydration';

import A1MaterialsSection from './A1MaterialsSection';
import {syncA3FacilitiesFromProduction} from './a3FacilitySync';
import A3ManufacturingSection from './A3ManufacturingSection';
import BUseSection from './BUseSection';
import CEndOfLifeSection from './CEndOfLifeSection';
import {
  getProductionFromForm,
  isProductionDataComplete,
  notifySiteUnitsSumMismatchIfNeeded
} from './lciProductionUtils';
import {
  type LciContinueValidation,
  validateLciBeforeContinue,
  validateLciTabBeforeContinue
} from './lciValidation';
import PCFFooterBackButton from './PCFFooterBackButton';
import PCFFooterPrimaryButton from './PCFFooterPrimaryButton';
import {runPCFDraftSave} from './pcfFormDraft';
import PCFFormHeader from './PCFFormHeader';
import {
  getNextLciSubTab,
  isLastLciSubTab,
  LCI_SUBTAB_TO_FIELD_PREFIX,
  type LciSubTabId,
  validateMountedFieldsForPrefix
} from './pcfFormValidation';
import ProductionDataSection from './ProductionDataSection';
import SubTabPills from './SubTabPills';
import TabStepper from './TabStepper';
import TransportSection from './TransportSection';

type SubTabId = LciSubTabId;

const SUB_TABS: Array<{id: SubTabId; label: string}> = [
  {id: 'prod', label: 'Production Data'},
  {id: 'a1', label: 'A1'},
  {id: 'a2', label: 'A2'},
  {id: 'a3', label: 'A3'},
  {id: 'a4', label: 'A4'},
  {id: 'b', label: 'B'},
  {id: 'c', label: 'C'}
];

const lciValidateTrigger: Array<'onSubmit'> = ['onSubmit'];

function tabsFromErrorFields(
  errorFields: Array<{name: Array<string | number>}>
): SubTabId[] {
  const tabs = new Set<SubTabId>();
  errorFields.forEach(({name}) => {
    const prefix = name[0] as LciFormTabKey | undefined;
    if (prefix && LCI_FIELD_PREFIX_TO_SUBTAB[prefix]) {
      tabs.add(LCI_FIELD_PREFIX_TO_SUBTAB[prefix]);
    }
  });

  return SUB_TABS.filter(({id}) => tabs.has(id)).map(({id}) => id);
}

const LCIDataCollectionPage: React.FC = () => {
  const [form] = Form.useForm();
  const [activeSubTab, setActiveSubTab] = useState<SubTabId>('prod');
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitErrorTab, setSubmitErrorTab] = useState<SubTabId | null>(null);
  const recordData = usePCFStore((state) => state.recordData);

  const {isSaving, saveDraftLabel, actionLabel, persist, runWhileSaving} =
    usePCFPersist();
  const {recordId, isStepReady, navigateToStep, reloadFromServer} =
    usePCFStepHydration(form, 'lci');

  Form.useWatch('production', form);
  const production = getProductionFromForm(form, recordData.lci?.production);
  const productionComplete = isProductionDataComplete(production);
  const productionFallback = recordData.lci?.production;
  const productionSyncKey = useMemo(
    () =>
      [
        production?.setup,
        production?.sites?.length ?? 0,
        production?.allocationBasis,
        ...(production?.sites ?? []).map(
          (site) =>
            `${site.name ?? ''}|${site.state ?? ''}|${site.facilityThroughput ?? ''}`
        )
      ].join('::'),
    [
      production?.setup,
      production?.allocationBasis,
      production?.sites?.length,
      production?.sites
        ?.map(
          (site) =>
            `${site.name ?? ''}|${site.state ?? ''}|${site.facilityThroughput ?? ''}`
        )
        .join('::')
    ]
  );

  useEffect(() => {
    syncA3FacilitiesFromProduction(form, productionFallback);
  }, [form, productionFallback, productionSyncKey]);

  const tabsWithErrors = useMemo((): SubTabId[] => {
    if (!submitFailed) return [];
    const errors = form.getFieldsError();
    const fieldsWithErrors = errors
      .filter(({errors: errs}) => errs.length > 0)
      .map(({name}) => ({name: name as Array<string | number>}));

    const tabs = tabsFromErrorFields(fieldsWithErrors);

    if (submitErrorTab && !tabs.includes(submitErrorTab)) {
      return [...tabs, submitErrorTab];
    }

    return tabs;
  }, [submitFailed, form, submitErrorTab]);

  const disabledTabs = useMemo((): SubTabId[] => {
    if (productionComplete) {
      return [];
    }

    if (submitErrorTab === 'a3' || tabsWithErrors.includes('a3')) {
      return [];
    }

    return ['a3'];
  }, [productionComplete, submitErrorTab, tabsWithErrors]);

  const showFormErrorSummary =
    submitFailed &&
    (form.getFieldsError().some(({errors}) => errors.length > 0) ||
      Boolean(submitMessage));

  const persistLci = async (status: 'draft' | 'approved'): Promise<void> => {
    await persist({
      section: 'lci',
      sectionValues: form.getFieldsValue(true) as LciFormValues,
      status
    });
  };

  const applyValidationFailure = (
    validationResult: LciContinueValidation,
    values: LciFormValues,
    options: {jumpToErrorTab: boolean}
  ): void => {
    setSubmitMessage(validationResult.message);
    setSubmitErrorTab(validationResult.tab);
    setSubmitFailed(true);

    if (validationResult.tab === 'prod') {
      notifySiteUnitsSumMismatchIfNeeded(values.production);
    }

    if (validationResult.errorFields?.length) {
      form.setFields(validationResult.errorFields);
    }

    if (validationResult.facilityIndex != null) {
      form.setFieldValue(
        ['a3', 'activeFacility'],
        validationResult.facilityIndex
      );
    }

    if (
      options.jumpToErrorTab &&
      validationResult.tab !== activeSubTab
    ) {
      if (validationResult.tab === 'a3') {
        syncA3FacilitiesFromProduction(form, productionFallback);
      }
      setActiveSubTab(validationResult.tab);
    }
  };

  const handleSaveDraft = (): void => {
    runWhileSaving(async () => {
      setSubmitFailed(false);
      setSubmitMessage(null);
      setSubmitErrorTab(null);
      syncA3FacilitiesFromProduction(form, productionFallback);
      await runPCFDraftSave(form, async () => {
        await persistLci('draft');
      });
    }).catch(() => {});
  };

  const handleSaveContinue = (): void => {
    runWhileSaving(async () => {
      const fieldPrefix = LCI_SUBTAB_TO_FIELD_PREFIX[activeSubTab];

      if (activeSubTab === 'prod' || activeSubTab === 'a3') {
        syncA3FacilitiesFromProduction(form, productionFallback);
      }

      await validateMountedFieldsForPrefix(form, fieldPrefix);
      const values = form.getFieldsValue(true) as LciFormValues;

      const tabValidation = validateLciTabBeforeContinue(values, activeSubTab);

      if (tabValidation) {
        applyValidationFailure(tabValidation, values, {jumpToErrorTab: false});
        throw Object.assign(new Error(tabValidation.message), {
          errorFields: [],
          crossTabValidation: true
        });
      }

      if (isLastLciSubTab(activeSubTab)) {
        const fullValidation = validateLciBeforeContinue(values);

        if (fullValidation) {
          applyValidationFailure(fullValidation, values, {jumpToErrorTab: true});
          throw Object.assign(new Error(fullValidation.message), {
            errorFields: [],
            crossTabValidation: true,
            fullStepValidation: true
          });
        }

        setSubmitFailed(false);
        setSubmitMessage(null);
        setSubmitErrorTab(null);
        await persistLci('approved');
        navigateToStep('impactConfiguration');

        return;
      }

      setSubmitFailed(false);
      setSubmitMessage(null);
      setSubmitErrorTab(null);
      await persistLci('draft');

      const nextTab = getNextLciSubTab(activeSubTab);

      if (nextTab === 'a3') {
        syncA3FacilitiesFromProduction(form, productionFallback);
      }

      if (nextTab) {
        setActiveSubTab(nextTab);
      }
    }).catch(
      (errorInfo: {
        errorFields?: Array<{name: Array<string | number>}>;
        crossTabValidation?: boolean;
      }) => {
        setSubmitFailed(true);
        if (!errorInfo.crossTabValidation) {
          setSubmitMessage(null);
          setSubmitErrorTab(null);
        }
        const errorTabs = tabsFromErrorFields(errorInfo.errorFields ?? []);
        const firstErrorTab = errorTabs[0];

        if (firstErrorTab === 'prod' || errorTabs.includes('prod')) {
          notifySiteUnitsSumMismatchIfNeeded(
            getProductionFromForm(form, productionFallback)
          );
        }

        if (
          firstErrorTab &&
          firstErrorTab !== activeSubTab &&
          !errorInfo.crossTabValidation
        ) {
          setActiveSubTab(firstErrorTab);
        }
        setTimeout(() => {
          document
            .querySelector('.ant-form-item-has-error, .field-error')
            ?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 100);
      }
    );
  };

  const renderSection = () => {
    switch (activeSubTab) {
      case 'prod':
        return <ProductionDataSection />;
      case 'a1':
        return <A1MaterialsSection />;
      case 'a2':
        return (
          <TransportSection
            variant="inbound"
            sectionTitle="A2 · Inbound Transport"
            addLabel="Add Supplier Origin"
            cardTitlePrefix="Supplier Logistics"
            listBasePath={['a2']}
          />
        );
      case 'a3':
        return <A3ManufacturingSection />;
      case 'a4':
        return (
          <TransportSection
            variant="outbound"
            sectionTitle="A4 · Distribution to Customer"
            addLabel="Add Distribution"
            cardTitlePrefix="Distribution Leg"
            listBasePath={['a4']}
          />
        );
      case 'b':
        return <BUseSection />;
      case 'c':
        return <CEndOfLifeSection />;
      default:
        return null;
    }
  };

  return (
    <Flex
      vertical
      justify="space-between"
      align="center"
      gap={24}
      style={{height: '100%'}}
    >
      <Flex
        vertical={false}
        justify="space-between"
        align="center"
        style={{width: '100%'}}
      >
        <MainTableTitle
          title="Product Carbon Footprint (PCF)"
          showDivider={false}
        />
        <Flex vertical={false} gap={10} align="center" justify="center">
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => {
              reloadFromServer().catch(() => {});
            }}
            disabled={isSaving}
          />
          <Button
            type="primary"
            htmlType="button"
            onClick={handleSaveDraft}
            loading={isSaving}
            disabled={isSaving}
          >
            {saveDraftLabel}
          </Button>
        </Flex>
      </Flex>
      <TabStepper activeStep={2} completedSteps={[1]} recordId={recordId} />
      <PageContent>
        <div className="lci-data-collection-page">
          {!isStepReady ? (
            <Loader topMargin />
          ) : (
            <>
          <PCFFormHeader
            title="2. Life Cycle Inventory (LCI) Data Collection"
            description="Fill in the data for the following life cycle stages."
          />
          <section className="card">
            <div className="card-body">
              <SubTabPills
                tabs={SUB_TABS}
                active={activeSubTab}
                onChange={(id) => {
                  if (id === 'a3') {
                    syncA3FacilitiesFromProduction(form, productionFallback);
                  }
                  setActiveSubTab(id as SubTabId);
                }}
                errorTabs={tabsWithErrors}
                disabledTabs={disabledTabs}
              />
              <Form
                form={form}
                layout="vertical"
                initialValues={lciFormInitialValues}
                validateTrigger={lciValidateTrigger}
                scrollToFirstError={{behavior: 'smooth', block: 'center'}}
                onValuesChange={() => {
                  if (submitFailed) {
                    setSubmitFailed(false);
                    setSubmitMessage(null);
                    setSubmitErrorTab(null);
                  }
                }}
              >
                {renderSection()}
              </Form>
            </div>
          </section>

          {showFormErrorSummary && (
            <div className="form-error-summary">
              {submitMessage ??
                (isLastLciSubTab(activeSubTab)
                  ? 'Please complete all mandatory fields across the highlighted sub-tabs before continuing.'
                  : 'Please complete all mandatory fields in this section before continuing.')}
            </div>
          )}

          <div className="footer-actions">
            <PCFFooterBackButton
              disabled={isSaving}
              onClick={() => {
                navigateToStep('goalAndScope');
              }}
            />
            <PCFFooterPrimaryButton
              onClick={handleSaveContinue}
              label={actionLabel('Save & Continue')}
              isSaving={isSaving}
            />
          </div>
            </>
          )}
        </div>
      </PageContent>
    </Flex>
  );
};

export default LCIDataCollectionPage;
